import { GridColDef } from '@mui/x-data-grid-pro';
import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import useSWR from 'swr';
import { dataGridCol } from '@shared/components/grid/common';
import { formatCurrency, formatString, gridDateTimeFormatter, withQueryParams } from '@shared/commonFunctions';
import { PhoenixGrid } from '@shared/components/grid/PhoenixGrid';
import { SERVICES } from '@shared/constants/apiConfig';

import { Wrapper } from '@styles/CommonStyles';
import { useTabs } from '@shared/hooks/useTabs';
import { withOpenTab } from '@shared/HOCs/withOpenTab';
import { TabIndexEnum } from '@modules/ContactManager/components/Header/interfaces';
import { IProductCancellations } from './ImportProductCancel';

const columns: GridColDef[] = [
  dataGridCol('contractNumber', {
    flex: 2,
    minWidth: 110,
  }),
  dataGridCol('product', {
    flex: 1,
    minWidth: 70,
  }),
  dataGridCol('lenderName', {
    headerName: 'Lender',
    flex: 1,
    minWidth: 100,
  }),
  dataGridCol('contractHolder', {
    flex: 2,
    minWidth: 120,
  }),
  dataGridCol('vinLast8', {
    flex: 1,
    minWidth: 100,
    valueFormatter: ({ value }) => (value?.toUpperCase()),
  }),
  dataGridCol('saleDate', {
    flex: 1,
    minWidth: 150,
    type: 'dateTime',
    valueFormatter: gridDateTimeFormatter,
  }),
  dataGridCol('cancelDate', {
    flex: 1,
    minWidth: 150,
    type: 'dateTime',
    valueFormatter: gridDateTimeFormatter,
  }),
  dataGridCol('cancelFactor', {
    flex: 1,
    minWidth: 100,
  }),
  dataGridCol('cancelReason', {
    flex: 2,
    minWidth: 100,
  }),
  dataGridCol('originalCustomerCost', {
    flex: 1,
    minWidth: 100,
    valueFormatter: ({ value }) => formatCurrency(value),
  }),
  dataGridCol('originalSellerCost', {
    flex: 1,
    minWidth: 100,
    valueFormatter: ({ value }) => formatCurrency(value),
  }),
  dataGridCol('cancelFee', {
    flex: 1,
    minWidth: 100,
    valueFormatter: ({ value }) => formatCurrency(value),
  }),
  dataGridCol('customerRefund', {
    flex: 1,
    valueFormatter: ({ value }) => formatCurrency(value),
  }),
  dataGridCol('refundFromSeller', {
    flex: 1,
    valueFormatter: ({ value }) => formatCurrency(value),
  }),
  dataGridCol('paymentAmount', {
    flex: 1,
    minWidth: 100,
    valueFormatter: ({ value }) => formatCurrency(value),
  }),
].map((column) => ({ minWidth: 100, ...column }));

const PhoenixDealsPage = () => {
  const navigate = useNavigate();
  const { openTab } = useTabs();

  const { data = [] } = useSWR<IProductCancellations[]>(
    `${SERVICES.ACCOUNTING}/productcancellations/checks?displayChecksValue=1`
  );

  const rows = useMemo(() => data.map((value) => ({ ...value, product: formatString(value.product) })), [data]);

  const handleRowClick = useCallback(({ row }: { row: IProductCancellations }) => {
    const route = withQueryParams('/contact-manager', {
      id: row.contactId,
      tab: TabIndexEnum.Deals,
      dealId: row.dealId,
      productCancellationCheckId: row.productCancellationCheckId,
    });
    const title = ' ';
    openTab({ title, route });
    navigate(route);
  }, []);

  return (
    <Wrapper>
      <PhoenixGrid
        autoHeight
        withHeader
        noButton
        strict
        canNotEdit
        canNotCopy
        name="Phoenix Deals"
        columns={columns}
        rows={rows || []}
        getRowId={(row) => row.productCancellationCheckId}
        onRowClick={handleRowClick}
      />
    </Wrapper>
  );
};

export const PhoenixDeals = withOpenTab(PhoenixDealsPage, 'Phoenix Deals'); 